import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  username?: string;
  avatar?: string;
  role: string;
  department?: string;
  phone?: string;
  gender?: 'male' | 'female' | 'other';
  dateOfBirth?: string;
  communicationAddress?: string;
  workLocation: {
    district: string;
    mandal: string;
    startDate: string;
  };
  supervisor: {
    name: string;
    role: string;
    email: string;
  };
  familyMembers: Array<{
    id: string;
    name: string;
    relationship: string;
    age?: number;
    occupation?: string;
  }>;
  education: Array<{
    id: string;
    level: 'School' | 'Intermediate' | 'Degree' | 'Post Graduate';
    institution: string;
    year: string;
    percentage?: number;
  }>;
  skills: Array<{
    id: string;
    name: string;
    level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
    category: string;
  }>;
  workExperience: Array<{
    id: string;
    company: string;
    position: string;
    startDate: string;
    endDate?: string;
    description: string;
  }>;
  socialProfiles: Array<{
    id: string;
    platform: string;
    url: string;
    username: string;
  }>;
  preferences: {
    language: string;
    timezone: string;
    theme: string;
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
  };
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
}

interface UserState {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
}

interface UserActions {
  // Profile management
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  updateAvatar: (avatar: string) => Promise<void>;
  removeAvatar: () => void;
  
  // Family members
  addFamilyMember: (member: Omit<UserProfile['familyMembers'][0], 'id'>) => void;
  updateFamilyMember: (memberId: string, updates: Partial<UserProfile['familyMembers'][0]>) => void;
  removeFamilyMember: (memberId: string) => void;
  
  // Education
  addEducation: (education: Omit<UserProfile['education'][0], 'id'>) => void;
  updateEducation: (educationId: string, updates: Partial<UserProfile['education'][0]>) => void;
  removeEducation: (educationId: string) => void;
  
  // Skills
  addSkill: (skill: Omit<UserProfile['skills'][0], 'id'>) => void;
  updateSkill: (skillId: string, updates: Partial<UserProfile['skills'][0]>) => void;
  removeSkill: (skillId: string) => void;
  
  // Work experience
  addWorkExperience: (experience: Omit<UserProfile['workExperience'][0], 'id'>) => void;
  updateWorkExperience: (experienceId: string, updates: Partial<UserProfile['workExperience'][0]>) => void;
  removeWorkExperience: (experienceId: string) => void;
  
  // Social profiles
  addSocialProfile: (profile: Omit<UserProfile['socialProfiles'][0], 'id'>) => void;
  updateSocialProfile: (profileId: string, updates: Partial<UserProfile['socialProfiles'][0]>) => void;
  removeSocialProfile: (profileId: string) => void;
  
  // Preferences
  updatePreferences: (preferences: Partial<UserProfile['preferences']>) => void;
  
  // State management
  setProfile: (profile: UserProfile | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  
  // Utility methods
  getProfileById: (id: string) => UserProfile | undefined;
  searchProfile: (query: string) => UserProfile[];
}

export const useUserStore = create<UserState & UserActions>()(
  persist(
    (set, get) => ({
      // Initial state
      profile: null,
      isLoading: false,
      error: null,

      // Profile management
      updateProfile: async (updates) => {
        set({ isLoading: true, error: null });
        try {
          const currentProfile = get().profile;
          if (currentProfile) {
            const updatedProfile = {
              ...currentProfile,
              ...updates,
              updatedAt: new Date().toISOString()
            };
            set({ profile: updatedProfile, isLoading: false });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update profile',
            isLoading: false
          });
        }
      },

      updateAvatar: async (avatar) => {
        try {
          const currentProfile = get().profile;
          if (currentProfile) {
            set({
              profile: { ...currentProfile, avatar, updatedAt: new Date().toISOString() }
            });
          }
        } catch (error) {
          set({ error: 'Failed to update avatar' });
        }
      },

      removeAvatar: () => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: { ...currentProfile, avatar: undefined, updatedAt: new Date().toISOString() }
          });
        }
      },

      // Family members
      addFamilyMember: (member) => {
        const newMember = { ...member, id: Date.now().toString() };
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              familyMembers: [...currentProfile.familyMembers, newMember],
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      updateFamilyMember: (memberId, updates) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              familyMembers: currentProfile.familyMembers.map(member =>
                member.id === memberId ? { ...member, ...updates } : member
              ),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      removeFamilyMember: (memberId) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              familyMembers: currentProfile.familyMembers.filter(member => member.id !== memberId),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      // Education
      addEducation: (education) => {
        const newEducation = { ...education, id: Date.now().toString() };
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              education: [...currentProfile.education, newEducation],
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      updateEducation: (educationId, updates) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              education: currentProfile.education.map(edu =>
                edu.id === educationId ? { ...edu, ...updates } : edu
              ),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      removeEducation: (educationId) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              education: currentProfile.education.filter(edu => edu.id !== educationId),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      // Skills
      addSkill: (skill) => {
        const newSkill = { ...skill, id: Date.now().toString() };
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              skills: [...currentProfile.skills, newSkill],
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      updateSkill: (skillId, updates) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              skills: currentProfile.skills.map(skill =>
                skill.id === skillId ? { ...skill, ...updates } : skill
              ),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      removeSkill: (skillId) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              skills: currentProfile.skills.filter(skill => skill.id !== skillId),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      // Work experience
      addWorkExperience: (experience) => {
        const newExperience = { ...experience, id: Date.now().toString() };
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              workExperience: [...currentProfile.workExperience, newExperience],
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      updateWorkExperience: (experienceId, updates) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              workExperience: currentProfile.workExperience.map(exp =>
                exp.id === experienceId ? { ...exp, ...updates } : exp
              ),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      removeWorkExperience: (experienceId) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              workExperience: currentProfile.workExperience.filter(exp => exp.id !== experienceId),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      // Social profiles
      addSocialProfile: (profile) => {
        const newSocialProfile = { ...profile, id: Date.now().toString() };
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              socialProfiles: [...currentProfile.socialProfiles, newSocialProfile],
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      updateSocialProfile: (profileId, updates) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              socialProfiles: currentProfile.socialProfiles.map(profile =>
                profile.id === profileId ? { ...profile, ...updates } : profile
              ),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      removeSocialProfile: (profileId) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              socialProfiles: currentProfile.socialProfiles.filter(profile => profile.id !== profileId),
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      // Preferences
      updatePreferences: (preferences) => {
        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              preferences: { ...currentProfile.preferences, ...preferences },
              updatedAt: new Date().toISOString()
            }
          });
        }
      },

      // State management
      setProfile: (profile) => set({ profile }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      // Utility methods
      getProfileById: (id) => {
        const profile = get().profile;
        return profile?.id === id ? profile : undefined;
      },

      searchProfile: (query) => {
        const profile = get().profile;
        if (!profile || !query) return [];
        
        const searchTerm = query.toLowerCase();
        const results: UserProfile[] = [];
        
        // Search in basic fields
        if (profile.name.toLowerCase().includes(searchTerm) ||
            profile.email.toLowerCase().includes(searchTerm) ||
            profile.department?.toLowerCase().includes(searchTerm)) {
          results.push(profile);
        }
        
        return results;
      }
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        profile: state.profile
      })
    }
  )
);

